import React, { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { Loader2, XCircle, RefreshCw, User, CreditCard } from 'lucide-react';

interface CanceledSubscriptionRow {
  id: string;
  userId: string;
  userName?: string;
  userEmail?: string;
  planId: string;
  planName: string;
  asaasSubscriptionId?: string;
  value: number;
  billingType?: string;
  sessionLabel?: string | null;
  canceledAt?: number;
  canceledBy?: string;
  createdAt?: number;
}

const formatDate = (epochMs?: number): string => {
  if (!epochMs) return '—';
  return new Date(epochMs).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatMoney = (value: number): string =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const CanceledSubscriptionsPanel: React.FC = () => {
  const [rows, setRows] = useState<CanceledSubscriptionRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const callable = httpsCallable<
        { limit?: number },
        { subscriptions: CanceledSubscriptionRow[] }
      >(functions, 'listCanceledSubscriptionsForAdmin');
      const result = await callable({ limit: 200 });
      setRows(result.data.subscriptions || []);
    } catch (err: any) {
      console.error('Erro ao carregar assinaturas canceladas:', err);
      setError(err?.message || 'Não foi possível carregar as assinaturas canceladas');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = rows.filter((row) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      row.planName?.toLowerCase().includes(q) ||
      row.userName?.toLowerCase().includes(q) ||
      row.userEmail?.toLowerCase().includes(q) ||
      row.userId.toLowerCase().includes(q) ||
      row.asaasSubscriptionId?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg sm:text-xl font-semibold text-gray-800 flex items-center gap-2">
            <XCircle className="w-5 h-5 text-rose-600" />
            Assinaturas canceladas
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Cancelamentos feitos pelo usuário na plataforma. Cada evento gera auditoria e notificação por e-mail
            (Hostinger) para <span className="font-medium">contato@constelabrasil.com.br</span>.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={isLoading}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      </div>

      <input
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Buscar por plano, nome, e-mail ou ID..."
        className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
      />

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12 text-gray-600">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600 mr-2" />
          Carregando...
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center text-gray-600">
          Nenhuma assinatura cancelada encontrada.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-4 py-3 font-semibold text-gray-700">Cancelado em</th>
                <th className="px-4 py-3 font-semibold text-gray-700">Usuário</th>
                <th className="px-4 py-3 font-semibold text-gray-700">Plano</th>
                <th className="px-4 py-3 font-semibold text-gray-700">Valor</th>
                <th className="px-4 py-3 font-semibold text-gray-700">Asaas</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {filtered.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                    {formatDate(row.canceledAt)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900 flex items-center gap-1">
                      <User className="w-3.5 h-3.5 text-gray-400" />
                      {row.userName || '—'}
                    </div>
                    {row.userEmail && (
                      <div className="text-xs text-gray-500">{row.userEmail}</div>
                    )}
                    <div className="text-xs text-gray-400 font-mono mt-0.5">{row.userId}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-800">
                    {row.planName}
                    {row.sessionLabel && (
                      <div className="text-xs text-gray-500">{row.sessionLabel}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1 text-gray-800">
                      <CreditCard className="w-3.5 h-3.5 text-gray-400" />
                      {formatMoney(row.value)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs font-mono text-gray-500">
                    {row.asaasSubscriptionId || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CanceledSubscriptionsPanel;
